// Situação de uma peça no Fechamento de OS (ADR-0029): a conta do Backlog do
// hotel, quantas retidas precisam de motivo e o primeiro problema, se houver.
// Pura, para a Ficha mostrar e o "Fechar OS" recusar com o mesmo texto.
//
// A conta sai só dos números digitados — Backlog antes + (Conferida −
// Devolvida) —, nunca das classificações: é o que fica no Backlog quando
// nenhuma peça fica sem motivo, qualquer que seja a divisão entre peça antiga
// e nova (ADR-0025).

/** Trecho de texto; `{ n }` é um número que a tela destaca. */
export type Trecho = string | { readonly n: number };

export type ImpedimentoRetencao = "semDevolvida" | "retida" | "semMotivo";

export interface EntradaSituacaoRetencao {
  /** Backlog do Cliente neste Tipo logo antes desta devolução (ADR-0028). */
  backlogAntes: number;
  /** O que o hotel declarou na Comanda. */
  recebida: number;
  /** O que a Comanda rende: Conferida, senão Recebida. */
  disponivel: number;
  /** Total digitado em Devolvida (parte própria + carona). */
  devolvida: number;
  /** Retidas já classificadas em "+ Motivo de Retenção". */
  classificadas: number;
  /** Retidas em linhas de motivo ainda em branco (só na Ficha, antes de
   * escolher): não contam como classificadas, mas já são algo digitado. */
  pendentesDeMotivo?: number;
  /** Retida digitada na Coleta; vazia vale 0. */
  retidaDigitada: number | null;
}

export interface SituacaoRetencao {
  backlogAntes: number;
  /** Conferida − Devolvida; negativa na carona. */
  retidaNaOS: number;
  /** Backlog do hotel depois desta devolução (o real). */
  total: number;
  /**
   * Retidas que o hotel não declarou: a Conferida passou da Recebida e essas
   * peças a mais ficaram (não voltaram). Estão no Backlog real, mas não no
   * papel do hotel — a Retida digitada é comparada sem elas (OS 684, opção A
   * do dono).
   */
  naoDeclaradas: number;
  /** Retidas que precisam de motivo, no mínimo. */
  minimo: number;
  /** Quanto falta classificar para chegar ao mínimo. */
  faltaClassificar: number;
  /** Sugestão do "+ Motivo de Retenção": o máximo que ainda cabe, ou 1. */
  sugestao: number;
  /** O primeiro problema, na ordem: peça da Comanda sem nada digitado no
   * Fechamento, Retida digitada diferente, retidas sem motivo. */
  impedimento: ImpedimentoRetencao | null;
  conta: Trecho[];
  situacao: Trecho[];
  /** O texto com que o "Fechar OS" recusa: a situação, e quantas faltam
   * classificar quando é retida sem motivo. Vazio sem impedimento. */
  recusa: string;
}

export function situacaoRetencao(e: EntradaSituacaoRetencao): SituacaoRetencao {
  const retidaNaOS = e.disponivel - e.devolvida;
  const total = e.backlogAntes + retidaNaOS;
  const minimo = Math.max(0, retidaNaOS);
  const faltaClassificar = Math.max(0, minimo - e.classificadas);
  const pendentes = e.pendentesDeMotivo ?? 0;
  const sobra = total - e.classificadas - pendentes;
  const digitada = e.retidaDigitada ?? 0;
  // das peças a mais que o hotel não declarou, as que ficaram retidas
  const naoDeclaradas = Math.min(Math.max(0, e.disponivel - e.recebida), Math.max(0, retidaNaOS));
  const esperadoNaComanda = total - naoDeclaradas;
  const retidasTexto = (n: number): string => (n === 1 ? "retida" : "retidas");
  // a Comanda tem peça, e nada voltou nem ficou: a Devolvida não foi digitada
  const semDevolvida = e.disponivel > 0 && e.devolvida === 0 && e.classificadas + pendentes === 0;

  const conta: Trecho[] =
    retidaNaOS >= 0
      ? ["Backlog do hotel: Backlog (", { n: e.backlogAntes }, ") + retida na OS (", { n: retidaNaOS }, ") = ", { n: total }]
      : ["Backlog do hotel: Backlog (", { n: e.backlogAntes }, ") − devolvidas do Backlog (", { n: -retidaNaOS }, ") = ", { n: total }];

  let impedimento: ImpedimentoRetencao | null = null;
  let situacao: Trecho[] =
    naoDeclaradas === 0
      ? ["Tudo confere"]
      : ["Tudo confere (", { n: naoDeclaradas }, ` ${naoDeclaradas === 1 ? "retida" : "retidas"} que o hotel não declarou)`];
  if (semDevolvida) {
    impedimento = "semDevolvida";
    situacao = ["Falta digitar a Devolvida."];
  } else if (digitada !== esperadoNaComanda) {
    impedimento = "retida";
    situacao =
      naoDeclaradas === 0
        ? ["Retida digitada ", { n: digitada }, ", Backlog real é ", { n: total }, ". Corrija a Retida ou confira Conferida/Devolvida."]
        : [
            "Retida digitada ",
            { n: digitada },
            ", a comanda deveria dizer ",
            { n: esperadoNaComanda },
            " (Backlog real ",
            { n: total },
            ", menos ",
            { n: naoDeclaradas },
            ` ${retidasTexto(naoDeclaradas)} que o hotel não declarou). Corrija a Retida ou confira Conferida/Devolvida.`,
          ];
  } else if (faltaClassificar > 0) {
    impedimento = "semMotivo";
    situacao = ["Retidas sem motivo: classifique em + Motivo de Retenção."];
  }

  return {
    backlogAntes: e.backlogAntes,
    retidaNaOS,
    total,
    naoDeclaradas,
    minimo,
    faltaClassificar,
    sugestao: sobra > 0 ? sobra : 1,
    impedimento,
    conta,
    situacao,
    recusa:
      impedimento === null
        ? ""
        : emTexto(situacao) + (impedimento === "semMotivo" ? ` Faltam ${faltaClassificar}.` : ""),
  };
}

/** Os trechos como texto corrido, sem destaque — para mensagens de erro. */
export function emTexto(trechos: readonly Trecho[]): string {
  return trechos.map((t) => (typeof t === "string" ? t : String(t.n))).join("");
}
